import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as authApi from "@/api/auth";
import { installAuthTokenProvider } from "@/api/client";
import { leaveTo } from "@/lib/browser-nav";
import type { UserMe } from "@/types/api";

interface AuthContextValue {
  user: UserMe | null;
  accessToken: string | null;
  loading: boolean;
  /** Почему не восстановилась сессия при загрузке (`/refresh`), null — не
   *  было ошибки. Нужна экрану входа после возврата из SSO (`?sso=ok`): там
   *  провал `/refresh` — не «сессии нет», а сорванный вход, и причину надо
   *  показать. */
  restoreError: unknown;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserMe | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [restoreError, setRestoreError] = useState<unknown>(null);

  // `tokenRef` is the single source of truth for the axios interceptor.
  // React state (`accessToken`) only drives re-renders for UI bits that
  // depend on auth status; the interceptor reads `tokenRef.current` directly
  // so it sees the new token *synchronously* after refresh, without having
  // to wait for React to flush a re-render. See the race-condition note in
  // `client.ts` and plan hotfix 6.x for why this matters.
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = accessToken;

  const setToken = useCallback((t: string | null) => {
    tokenRef.current = t;
    setAccessToken(t);
  }, []);

  const doLogin = useCallback(
    async (username: string, password: string) => {
      const data = await authApi.login(username, password);
      setToken(data.access_token);
      setUser(data.user);
    },
    [setToken],
  );

  const doLogout = useCallback(async () => {
    let endSessionUrl: string | null = null;
    try {
      endSessionUrl = (await authApi.logout()).end_session_url;
    } catch (err) {
      // Нет end_session_url для принудительного выхода из Zitadel (см.
      // authApi.logout) — локальную сессию всё равно чистим ниже, но ошибку
      // не глотаем молча.
      console.error("Logout failed", err);
    }
    setToken(null);
    setUser(null);
    queryClient.clear();
    // SSO: своя сессия уже очищена — теперь выход из браузерной сессии
    // Zitadel, иначе следующий «Войти через SSO» пустит без пароля и 2FA.
    // Zitadel вернёт на `/login` (post_logout_redirect_uri задаёт бэкенд).
    if (endSessionUrl) leaveTo(endSessionUrl);
  }, [queryClient, setToken]);

  const doRefresh = useCallback(async (): Promise<string> => {
    const data = await authApi.refreshSession();
    setToken(data.access_token);
    setUser(data.user);
    return data.access_token;
  }, [setToken]);

  const doChangePassword = useCallback(async (current: string, next: string) => {
    const data = await authApi.changePassword(current, next);
    setToken(data.access_token);
    setUser(data.user);
  }, [setToken]);

  // Register the token provider once — axios needs closures that always see the
  // latest token, so we read through tokenRef instead of capturing state.
  useEffect(() => {
    installAuthTokenProvider({
      getAccessToken: () => tokenRef.current,
      onRefreshSuccess: (t) => setToken(t),
      onUnauthenticated: () => {
        setToken(null);
        setUser(null);
        queryClient.clear();
      },
      refresh: doRefresh,
    });
  }, [doRefresh, queryClient, setToken]);

  // Attempt silent refresh on mount — if the httpOnly cookie is still valid,
  // we get a new access token without the user retyping credentials.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await doRefresh();
      } catch (err) {
        // Нет живой сессии → остаёмся на входе. Ошибку храним: экран входа
        // показывает её только после возврата из SSO, где сессия обязана быть.
        if (!cancelled) setRestoreError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doRefresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      loading,
      restoreError,
      login: doLogin,
      logout: doLogout,
      changePassword: doChangePassword,
    }),
    [user, accessToken, loading, restoreError, doLogin, doLogout, doChangePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
